import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Star, User, AlertTriangle, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { UserRoleRules, AppRole } from '@/core/BusinessRulesEngine/rules/UserRoleRules';
import { AdminService } from '@/core/services/AdminService';
import { debugLogger } from '@/utils/debugLogger';

interface RoleManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  currentUserId: string;
  onRoleChanged: () => void;
}

const RoleManagementDialog: React.FC<RoleManagementDialogProps> = ({
  isOpen,
  onClose,
  user,
  currentUserId,
  onRoleChanged
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [currentRole, setCurrentRole] = useState<AppRole>('user');
  const [selectedRole, setSelectedRole] = useState<AppRole>('user');
  const [loadingCurrentRole, setLoadingCurrentRole] = useState(true);

  // Carregar role atual do usuário
  useEffect(() => {
    if (isOpen && user) {
      loadCurrentRole();
    }
  }, [isOpen, user]);

  const loadCurrentRole = async () => {
    setLoadingCurrentRole(true);
    try {
      debugLogger.debug('[RoleManagement]', 'Loading current role for user', user.id);
      
      // Verificar role atual
      const isAdmin = await AdminService.isAdmin(user.id);
      const isModerator = await AdminService.isModerator(user.id);
      
      let role: AppRole = 'user';
      if (isAdmin) {
        role = 'admin';
      } else if (isModerator) {
        role = 'moderator';
      }
      
      setCurrentRole(role);
      setSelectedRole(role);
      
      debugLogger.info('[RoleManagement]', 'Current role loaded', { userId: user.id, role });
    } catch (error) {
      debugLogger.error('[RoleManagement]', 'Error loading current role', error);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar role atual',
        variant: 'destructive'
      });
    } finally {
      setLoadingCurrentRole(false);
    }
  };

  const handleSaveRole = async () => {
    if (!user || selectedRole === currentRole) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      debugLogger.debug('[RoleManagement]', 'Attempting role change', {
        userId: user.id,
        oldRole: currentRole,
        newRole: selectedRole
      });

      // Validar via BRE
      const validation = await UserRoleRules.canChangeUserRole(
        { id: currentUserId } as any,
        { id: user.id } as any,
        selectedRole
      );

      if (!validation.allowed) {
        toast({
          title: 'Ação Negada',
          description: validation.reason,
          variant: 'destructive'
        });
        return;
      }

      // Remover role antiga
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // Inserir nova role (se não for 'user')
      if (selectedRole !== 'user') {
        const { error: insertError } = await supabase
          .from('user_roles')
          .insert({
            user_id: user.id,
            role: selectedRole
          });

        if (insertError) throw insertError;
      }

      // Invalidar cache do AdminService
      AdminService.invalidateUserCache(user.id);

      // Registrar auditoria
      await supabase
        .from('audit_logs')
        .insert({
          user_id: currentUserId,
          event_type: 'role_changed',
          metadata: {
            target_user_id: user.id,
            target_username: user.username,
            old_role: currentRole,
            new_role: selectedRole,
            timestamp: new Date().toISOString()
          }
        });

      debugLogger.info('[RoleManagement]', 'Role changed successfully', {
        userId: user.id,
        oldRole: currentRole,
        newRole: selectedRole
      });

      toast({
        title: 'Role Atualizada',
        description: `${user.username} agora é ${selectedRole === 'admin' ? 'Administrador' : selectedRole === 'moderator' ? 'Moderador' : 'Usuário'}`,
      });

      onRoleChanged();
      onClose();
    } catch (error) {
      debugLogger.error('[RoleManagement]', 'Error changing role', error);
      toast({
        title: 'Erro',
        description: 'Falha ao atualizar role do usuário',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role: AppRole) => {
    switch (role) {
      case 'admin': return <Shield className="h-5 w-5" />;
      case 'moderator': return <Star className="h-5 w-5" />;
      case 'user': return <User className="h-5 w-5" />;
    }
  };

  const getRoleDescription = (role: AppRole) => {
    switch (role) {
      case 'admin':
        return 'Controle total do sistema, incluindo gerenciamento de roles e todas as funcionalidades administrativas';
      case 'moderator':
        return 'Acesso ao painel administrativo com permissões limitadas (Overview, Usuários, Documentos, Mídia, Auditoria)';
      case 'user':
        return 'Usuário regular sem privilégios administrativos';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-black border-white/20">
        <DialogHeader>
          <DialogTitle className="text-white">Gerenciar Role</DialogTitle>
          <DialogDescription className="text-white/60">
            Alterar permissões de <span className="font-bold text-white">@{user?.username}</span>
          </DialogDescription>
        </DialogHeader>

        {loadingCurrentRole ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-white/60" />
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-white text-sm font-medium">Role Atual</Label>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10">
                  {getRoleIcon(currentRole)}
                  <span className="text-white font-medium">
                    {currentRole === 'admin' ? 'Administrador' : currentRole === 'moderator' ? 'Moderador' : 'Usuário'}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-white text-sm font-medium">Selecionar Nova Role</Label>
                <RadioGroup value={selectedRole} onValueChange={(value) => setSelectedRole(value as AppRole)}>
                  <div className="space-y-2">
                    {/* User Option */}
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-white/10 hover:bg-white/5 transition-colors">
                      <RadioGroupItem value="user" id="user" className="mt-1" />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="user" className="flex items-center gap-2 text-white cursor-pointer">
                          <User className="h-4 w-4" />
                          <span className="font-medium">Usuário</span>
                        </Label>
                        <p className="text-xs text-white/60 leading-relaxed">
                          {getRoleDescription('user')}
                        </p>
                      </div>
                    </div>

                    {/* Moderator Option */}
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-blue-500/30 hover:bg-blue-500/10 transition-colors">
                      <RadioGroupItem value="moderator" id="moderator" className="mt-1" />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="moderator" className="flex items-center gap-2 text-white cursor-pointer">
                          <Star className="h-4 w-4 text-blue-400" />
                          <span className="font-medium">Moderador</span>
                        </Label>
                        <p className="text-xs text-white/60 leading-relaxed">
                          {getRoleDescription('moderator')}
                        </p>
                      </div>
                    </div>

                    {/* Admin Option */}
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-red-500/30 hover:bg-red-500/10 transition-colors">
                      <RadioGroupItem value="admin" id="admin" className="mt-1" />
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="admin" className="flex items-center gap-2 text-white cursor-pointer">
                          <Shield className="h-4 w-4 text-red-400" />
                          <span className="font-medium">Administrador</span>
                        </Label>
                        <p className="text-xs text-white/60 leading-relaxed">
                          {getRoleDescription('admin')}
                        </p>
                      </div>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {selectedRole !== currentRole && (
                <Alert className="bg-yellow-500/10 border-yellow-500/30">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <AlertDescription className="text-yellow-200 text-sm">
                    Esta mudança entrará em vigor imediatamente. O usuário precisará fazer logout/login para ver as novas permissões.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveRole}
                disabled={loading || selectedRole === currentRole}
                className="bg-white text-black hover:bg-white/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar Mudança'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoleManagementDialog;
